import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { PlannerPages, DagbladToolbar } from './DagbladPlanner';
import { KidsColumn, applySticker } from './DagbladKids';
import DagbladWeekly from './DagbladWeekly';
import DagbladShopping from './DagbladShopping';
import ChoreBoard from './ChoreBoard';
import { AssistantBox, NextDayBox } from './DagbladAssistant';
import { toggleEventDone } from './SyncedEvents';
import { fireDayDone, StreakChip } from './DayDoneCelebration';
import { EditableLead, InlineAdd, MissingSections, addTodo, addEvent } from './DagbladInline';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const INK = '#2B2620';
export const OLIVE = '#6B7246';
export const PAPER = '#F4EEE1';
export const SERIF = "'Playfair Display', Georgia, serif";
export const BODY = "'Libre Baskerville', Georgia, serif";
const FONT_URL = 'https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,700;0,900;1,700&family=Libre+Baskerville:ital@0;1&display=swap';

function useFonts() {
  useEffect(() => {
    if (document.getElementById('dagblad-fonts')) return;
    const l = document.createElement('link');
    l.id = 'dagblad-fonts'; l.rel = 'stylesheet'; l.href = FONT_URL;
    document.head.appendChild(l);
  }, []);
}

export function Check({ checked, onChange, children, testid }) {
  return (
    <label className="flex items-start gap-2.5 py-1.5 cursor-pointer group" data-testid={testid}>
      <input type="checkbox" checked={checked} onChange={onChange} className="sr-only" />
      <span className="mt-0.5 w-5 h-5 flex-shrink-0 border-2 flex items-center justify-center transition-colors"
        style={{ borderColor: INK, background: checked ? OLIVE : 'transparent' }}>
        {checked && <span className="text-[#F4EEE1] text-xs font-black">✓</span>}
      </span>
      <span className={`text-[15px] leading-snug transition-opacity ${checked ? 'line-through opacity-45' : ''}`} style={{ fontFamily: BODY }}>{children}</span>
    </label>
  );
}

export function Column({ title, children, testid, onHide }) {
  return (
    <section className="py-4 border-t-2" style={{ borderColor: INK }} data-testid={testid}>
      <div className="flex items-start justify-between gap-2 mb-2">
        <h3 className="text-[11px] font-black uppercase tracking-[0.3em]" style={{ color: OLIVE }}>{title}</h3>
        {onHide && <button onClick={onHide} className="no-print text-[10px] font-bold opacity-40 hover:opacity-100 whitespace-nowrap" title="Ta bort rutan ur tidningen" data-testid={`${testid}-hide`}>✕ dölj</button>}
      </div>
      {children}
    </section>
  );
}

const EDITION = {
  morning: { name: 'Elsas Dagblad', tag: 'Morgonupplagan · läst med kaffe', ribbon: '#8C2F2F',
    marquee: 'EXTRA EXTRA! · BARA HÄR OCH NU · EXTRA EXTRA! · RESTEN FÅR VÄNTA · EXTRA EXTRA! · BARA HÄR OCH NU ·' },
  evening: { name: 'Elsas Kvällsblad', tag: 'Kvällsupplagan · imorgon, redan ikväll', ribbon: '#2E3A59',
    marquee: 'EXTRA EXTRA! · IMORGON · LÄGG FRAM IKVÄLL · EXTRA EXTRA! · SOV GOTT · EXTRA EXTRA! · IMORGON ·' },
  weekly: { name: 'Elsas Söndagsbilaga', tag: 'Veckotidningen · se tillbaka, blicka framåt', ribbon: '#6B7246',
    marquee: 'EXTRA EXTRA! · VECKANS BÄSTA · NÄSTA VECKA · EXTRA EXTRA! · EN DAG I TAGET · EXTRA EXTRA! · VECKANS BÄSTA ·' },
};

const PRINT_CSS = `@keyframes marquee{from{transform:translateX(0)}to{transform:translateX(-50%)}}
@media print{@page{size:A4;margin:10mm}html,body{height:auto!important;background:#fff!important}
body *{visibility:hidden!important}[data-print-root],[data-print-root] *{visibility:visible!important}
[data-testid="dagblad-front-page"]{position:static!important;overflow:visible!important;background:#fff!important}
[data-print-root]{position:absolute;left:0;top:0;width:100%;box-shadow:none!important}.no-print{display:none!important}}`;

function Masthead({ p }) {
  const ed = EDITION[p.edition];
  return (
    <header className="text-center">
      <div className="overflow-hidden whitespace-nowrap py-1.5 text-[#F4EEE1] text-xs font-black tracking-[0.4em]" style={{ background: ed.ribbon }} data-testid="dagblad-extra">
        <span className="inline-block animate-[marquee_18s_linear_infinite]">{ed.marquee}</span>
      </div>
      <h1 className="mt-4 text-[2.5rem] min-[400px]:text-5xl sm:text-6xl lg:text-7xl font-black leading-none break-words" style={{ fontFamily: SERIF, color: INK }} data-testid="dagblad-masthead">{ed.name}</h1>
      <p className="mt-1 italic text-sm" style={{ fontFamily: BODY, color: OLIVE }} data-testid="dagblad-edition-tag">Grundad av Farmor Elsa · {ed.tag}</p>
      <div className="mt-3 flex flex-wrap justify-between gap-x-4 gap-y-1 border-y-[3px] border-double py-1.5 text-[11px] font-bold uppercase tracking-widest" style={{ borderColor: INK, color: INK }}>
        <span data-testid="dagblad-issue">Nr {p.issue_no}</span>
        <span data-testid="dagblad-date">{p.date_pretty}</span>
        <span data-testid="dagblad-namesday">Namnsdag: {p.namesday || '–'}</span>
        <span>Pris: en kram</span>
      </div>
    </header>
  );
}

function Lead({ ed }) {
  if (!ed) {
    return (
      <div className="py-6 space-y-3 animate-pulse" data-testid="dagblad-editorial-loading">
        <div className="h-10 w-4/5 bg-black/10" /><div className="h-4 w-full bg-black/10" /><div className="h-4 w-11/12 bg-black/10" />
        <p className="italic text-sm" style={{ fontFamily: BODY, color: OLIVE }}>Farmor Elsa skriver förstasidan…</p>
      </div>
    );
  }
  return (
    <div className="py-5" data-testid="dagblad-editorial">
      <h2 className="text-4xl sm:text-5xl font-black leading-[1.05]" style={{ fontFamily: SERIF, color: INK }} data-testid="dagblad-headline">{ed.headline}</h2>
      <p className="mt-3 text-base sm:text-lg leading-relaxed first-letter:text-5xl first-letter:font-black first-letter:float-left first-letter:mr-2 first-letter:leading-none"
        style={{ fontFamily: BODY, color: INK }} data-testid="dagblad-lead">{ed.lead}</p>
      <blockquote className="mt-4 pl-4 border-l-4 italic text-base" style={{ borderColor: OLIVE, fontFamily: SERIF, color: OLIVE }} data-testid="dagblad-quote">
        ”{ed.elsa_quote}” <span className="not-italic text-xs font-bold uppercase tracking-widest">— Farmor Elsa</span>
      </blockquote>
    </div>
  );
}

function CustomPoints({ p, has, tick, onAdd, onDelete, onHide }) {
  const [text, setText] = useState('');
  const [everyDay, setEveryDay] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    await onAdd(text.trim(), everyDay);
    setText('');
  };
  return (
    <Column title={p.edition === 'evening' ? 'Egna punkter till imorgon' : 'Egna punkter'} testid="dagblad-custom" onHide={onHide}>
      {p.custom.map((c) => (
        <div key={c.id} className="flex items-start gap-2 group/item" data-testid={`dagblad-custom-${c.id}`}>
          <div className="flex-1">
            <Check checked={has(`custom:${c.id}`)} onChange={() => tick(`custom:${c.id}`, !has(`custom:${c.id}`))} testid={`dagblad-custom-check-${c.id}`}>
              {c.text}{c.every_day && <span className="ml-2 text-[10px] uppercase tracking-widest" style={{ color: OLIVE }}>varje dag</span>}
            </Check>
          </div>
          <button onClick={() => onDelete(c.id)} className="text-xs opacity-40 hover:opacity-100 pt-2" aria-label="Ta bort" data-testid={`dagblad-custom-delete-${c.id}`}>✕</button>
        </div>
      ))}
      <form onSubmit={submit} className="no-print mt-2 flex flex-wrap items-center gap-2" data-testid="dagblad-custom-form">
        <input value={text} onChange={(e) => setText(e.target.value)} placeholder="Skriv en egen punkt…" maxLength={140}
          className="flex-1 min-w-[160px] bg-transparent border-b-2 px-1 py-1 text-[15px] outline-none" style={{ borderColor: INK, fontFamily: BODY }} data-testid="dagblad-custom-input" />
        <label className="flex items-center gap-1 text-xs font-bold" data-testid="dagblad-custom-everyday">
          <input type="checkbox" checked={everyDay} onChange={(e) => setEveryDay(e.target.checked)} style={{ accentColor: OLIVE }} /> Varje dag
        </label>
        <button type="submit" className="px-3 py-1.5 text-xs font-black uppercase tracking-widest text-[#F4EEE1]" style={{ background: OLIVE }} data-testid="dagblad-custom-add">Lägg till</button>
      </form>
    </Column>
  );
}

const stockholmHour = () => Number(new Date().toLocaleString('en-GB', { timeZone: 'Europe/Stockholm', hour: '2-digit', hour12: false }));

const goTab = (tab, sub) => {
  if (sub) { try { sessionStorage.setItem('minnes-subtab', sub); } catch {} }
  window.dispatchEvent(new CustomEvent('focus-critter:navigate', { detail: { tab } }));
};

function SectionPicker({ p, onSaved }) {
  const [open, setOpen] = useState(false);
  const [sel, setSel] = useState(p.sections);
  const save = async () => {
    try {
      await axios.put(`${API}/dagblad/sections`, { sections: sel }, { withCredentials: true });
      toast.success('🗞️ Tidningen fyller i det här åt dig från och med nu');
      setOpen(false); onSaved();
    } catch { toast.error('Kunde inte spara'); }
  };
  return (
    <div className="pt-3 text-center">
      <button onClick={() => { setSel(p.sections); setOpen(!open); }} className="text-xs font-black uppercase tracking-widest underline underline-offset-4" style={{ color: OLIVE }} data-testid="dagblad-sections-open">
        🗞️ Välj vad som ska stå i tidningen
      </button>
      {open && (
        <div className="mt-3 mx-auto max-w-xl text-left border-2 p-4" style={{ borderColor: INK, background: '#FBF7EE' }} data-testid="dagblad-sections-panel">
          <p className="text-sm italic mb-2" style={{ fontFamily: BODY }}>Kryssa i det du vill ha med, så hämtar Elsa det automatiskt varje dag.</p>
          <div className="grid sm:grid-cols-2 gap-x-4">
            {p.all_sections.map((x) => (
              <Check key={x.id} checked={sel.includes(x.id)} testid={`dagblad-section-${x.id}`}
                onChange={() => setSel(sel.includes(x.id) ? sel.filter((y) => y !== x.id) : [...sel, x.id])}>{x.emoji} {x.label}</Check>
            ))}
          </div>
          <button onClick={save} className="mt-3 px-4 py-2 text-xs font-black uppercase tracking-widest text-[#F4EEE1]" style={{ background: INK }} data-testid="dagblad-sections-save">Spara</button>
        </div>
      )}
    </div>
  );
}

function HpBox({ hp }) {
  return (
    <Column title="Hampus pluggruta" testid="dagblad-hp">
      <button onClick={() => goTab('minnes', 'hp')} className="text-left w-full" data-testid="dagblad-hp-open">
        <p className="text-[15px]" style={{ fontFamily: BODY }}>
          🐹 {hp.done ? `Dagens pass är klart: ${hp.score}/${hp.total} rätt!` : `Dagens HP-pass: ${hp.part.emoji} ${hp.part.name}, 5 små frågor`}
        </p>
        {hp.cards_due > 0 && <p className="text-sm mt-1" style={{ fontFamily: BODY }} data-testid="dagblad-hp-cards">🃏 {hp.cards_due} ordkort vill repeteras</p>}
        <span className="text-[11px] font-black uppercase tracking-widest" style={{ color: OLIVE }}>Öppna →</span>
      </button>
      {hp.gossip && (
        <div className="mt-2 pt-2 border-t border-dashed text-sm space-y-1.5" style={{ fontFamily: BODY, borderColor: OLIVE }} data-testid="dagblad-hp-gossip">
          <p data-testid="dagblad-hp-gossip-today">☕ <b>Farmors skvaller om {hp.part.name}, {hp.gossip.today.nick.toLowerCase()}:</b> {hp.gossip.today.trick}</p>
          <p className="opacity-80" data-testid="dagblad-hp-gossip-new">👀 <b>Dagens nya bekantskap, {hp.gossip.new.emoji} {hp.gossip.new.name}, {hp.gossip.new.nick.toLowerCase()}:</b> {hp.gossip.new.gossip}</p>
        </div>
      )}
    </Column>
  );
}

function ChallengesBox({ items }) {
  return (
    <Column title="Mina utmaningar" testid="dagblad-challenges">
      {items.map((c) => (
        <button key={c.slug} onClick={() => goTab('challenges')} className="flex items-center gap-2 py-1 w-full text-left" data-testid={`dagblad-challenge-${c.slug}`}>
          <span>{c.emoji}</span>
          <span className={`text-sm flex-1 ${c.done_today ? 'line-through opacity-50' : ''}`} style={{ fontFamily: BODY }}>{c.title}</span>
          <span className="text-[11px] font-black" style={{ color: OLIVE }}>{c.count}/{c.duration}</span>
        </button>
      ))}
    </Column>
  );
}

function EditionSwitch({ edition, onChange }) {
  return (
    <div className="grid grid-cols-3 sm:flex sm:justify-center gap-0 pt-4" data-testid="dagblad-edition-switch">
      {[['morning', '☀️', 'Morgon'], ['evening', '🌙', 'Kväll'], ['weekly', '📚', 'Vecko']].map(([id, icon, label]) => (
        <button key={id} onClick={() => onChange(id)} data-testid={`dagblad-edition-${id}`}
          className="min-w-0 px-1 sm:px-4 py-1.5 text-[10px] sm:text-xs font-black uppercase tracking-wider sm:tracking-widest border-2 transition-colors"
          style={{ borderColor: INK, background: edition === id ? INK : 'transparent', color: edition === id ? PAPER : INK }}>{icon} {label}<span className="hidden sm:inline">tidningen</span></button>
      ))}
    </div>
  );
}

export default function DagbladFrontPage({ overlay = false, onClose, initialEdition }) {
  useFonts();
  const [edition, setEdition] = useState(initialEdition || (stockholmHour() >= 18 ? 'evening' : 'morning'));
  const [p, setP] = useState(null);
  const [ed, setEd] = useState(null);
  const [version, setVersion] = useState(0);
  const lastEdition = useRef(null);

  useEffect(() => {
    let alive = true;
    if (lastEdition.current !== edition) { setP(null); setEd(null); lastEdition.current = edition; }
    if (edition === 'weekly') {
      setP({ edition: 'weekly', weekly: true, issue_no: 'Söndag', date_pretty: 'Veckans sammanfattning', namesday: '' });
      return () => { alive = false; };
    }
    axios.get(`${API}/dagblad/today`, { params: { edition }, withCredentials: true }).then((r) => {
      if (!alive) return;
      setP(r.data);
      if (r.data.editorial) setEd(r.data.editorial);
      if (!r.data.kid_jokes_ready && r.data.kid_column?.length) {
        axios.post(`${API}/dagblad/kid-jokes`, {}, { params: { edition }, withCredentials: true, timeout: 60000 }).then((j) => {
          if (alive) setP((cur) => (cur && cur.kid_column ? { ...cur, kid_column: cur.kid_column.map((k) => ({ ...k, joke: j.data[k.id] || k.joke })) } : cur));
        }).catch(() => {});
      }
      if (!r.data.editorial) {
        axios.post(`${API}/dagblad/editorial`, {}, { params: { edition }, withCredentials: true, timeout: 60000 })
          .then((e) => { if (alive) setEd(e.data); }).catch(() => {});
      }
    }).catch(() => { if (alive) toast.error('Tidningen kom inte fram idag'); });
    return () => { alive = false; };
  }, [edition, version]);
  useEffect(() => {
    if (!overlay) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  const tick = async (key, done) => {
    setP((cur) => ({ ...cur, checks: done ? [...cur.checks, key] : cur.checks.filter((k) => k !== key) }));
    try {
      const { data } = await axios.post(`${API}/dagblad/check`, { key, done, edition }, { withCredentials: true });
      if (data.sticker) gotSticker(data.sticker);
    } catch { toast.error('Kunde inte spara'); }
  };
  const gotSticker = (st) => {
    setP((cur) => applySticker(cur, st));
    window.dispatchEvent(new Event('focus-critter:rewards-changed'));
    toast.success(`${st.emoji} ${st.kid_name} fick dagens klistermärke!`);
  };
  const addCustom = async (text, everyDay) => {
    try {
      const { data } = await axios.post(`${API}/dagblad/custom`, { text, edition, every_day: everyDay }, { withCredentials: true });
      setP((cur) => ({ ...cur, custom: [...cur.custom, data] }));
    } catch (e) { toast.error(e?.response?.data?.detail || 'Kunde inte lägga till'); }
  };
  const saveSections = async (list) => {
    try { await axios.put(`${API}/dagblad/sections`, { sections: list }, { withCredentials: true }); setVersion((v) => v + 1); }
    catch { toast.error('Kunde inte ändra tidningen'); }
  };
  const hide = (id) => { saveSections(p.sections.filter((x) => x !== id)); toast(`Rutan är borttagen, du hittar den längst ner under "Saknar du något?"`); };
  const show = (id) => saveSections([...p.sections, id]);
  const resetEditorial = async () => {
    setEd(null);
    try {
      await axios.delete(`${API}/dagblad/editorial`, { params: { edition }, withCredentials: true });
      const r = await axios.post(`${API}/dagblad/editorial`, {}, { params: { edition }, withCredentials: true, timeout: 60000 });
      setEd(r.data); setP((cur) => ({ ...cur, custom_editorial: false }));
    } catch { toast.error('Elsa hann inte skriva om'); }
  };
  const reload = () => setVersion((v) => v + 1);
  const deleteCustom = async (id) => {
    setP((cur) => ({ ...cur, custom: cur.custom.filter((c) => c.id !== id) }));
    try { await axios.delete(`${API}/dagblad/custom/${id}`, { withCredentials: true }); } catch { toast.error('Kunde inte ta bort'); }
  };
  const tickEvent = async (i) => {
    const e = p.events[i];
    const flip = (val) => setP((cur) => ({ ...cur, events: cur.events.map((x, j) => (j === i ? { ...x, completed: val } : x)) }));
    flip(!e.completed);
    if (!e.completed && p.edition === 'morning' && p.events.every((x, j) => (j === i ? true : x.completed))) fireDayDone();
    try { await toggleEventDone(e); } catch { flip(e.completed); toast.error('Kunde inte spara'); }
  };
  const tickTodo = async (t) => {
    setP((cur) => ({ ...cur, top3: cur.top3.map((x) => (x.id === t.id ? { ...x, done: !t.done } : x)) }));
    try { await axios.patch(`${API}/daily-flow/items/${t.id}`, { done: !t.done }, { withCredentials: true }); } catch { toast.error('Kunde inte spara'); }
  };
  const close = () => {
    axios.post(`${API}/dagblad/seen`, { edition }, { withCredentials: true }).catch(() => {});
    onClose?.();
  };

  const wrap = overlay ? 'fixed inset-0 z-[300] overflow-y-auto overflow-x-hidden' : 'rounded-sm overflow-x-hidden';
  if (!p) return <div className={wrap} style={{ background: PAPER }} data-testid="dagblad-loading"><div className="h-64 animate-pulse" /></div>;
  if (p.weekly) {
    return (
      <div className={wrap} style={{ background: PAPER, color: INK }} data-testid="dagblad-front-page">
        <style>{PRINT_CSS}</style>
        <article className="relative max-w-4xl mx-auto px-5 sm:px-8 pb-10 pt-0" data-print-root>
          {overlay && <button onClick={close} className="no-print absolute right-3 top-9 z-10 w-8 h-8 border-2 font-black" style={{ borderColor: INK, background: PAPER }} aria-label="Stäng tidningen" data-testid="dagblad-close-top">✕</button>}
          {!overlay && <div className="no-print"><EditionSwitch edition={edition} onChange={setEdition} /></div>}
          <Masthead p={p} />
          <DagbladWeekly />
          <footer className="mt-8 pt-4 border-t-[3px] border-double text-center" style={{ borderColor: INK }}>
            <button onClick={() => window.print()} className="no-print mr-2 px-5 py-3 text-sm font-black uppercase tracking-widest border-2" style={{ borderColor: INK }} data-testid="weekly-print">🖨️ Skriv ut</button>
            {overlay && <button onClick={close} className="no-print px-8 py-3 text-sm font-black uppercase tracking-widest text-[#F4EEE1]" style={{ background: INK }} data-testid="dagblad-close">Ha en fin söndag ☕</button>}
          </footer>
        </article>
      </div>
    );
  }
  const has = (k) => p.checks.includes(k);
  const on = (id) => p.sections.includes(id);
  const w = p.weather;

  return (
    <div className={wrap} style={{ background: PAPER, color: INK, backgroundImage: 'radial-gradient(rgba(43,38,32,0.05) 1px, transparent 1px)', backgroundSize: '4px 4px' }} data-testid="dagblad-front-page">
      <style>{PRINT_CSS}</style>
      <article className="relative max-w-4xl mx-auto px-5 sm:px-8 pb-10 pt-0 shadow-[0_0_0_1px_rgba(43,38,32,0.15)]" data-print-root>
        {overlay && (
          <button onClick={close} className="no-print absolute right-3 top-9 z-10 w-8 h-8 border-2 font-black transition-transform hover:scale-110" style={{ borderColor: INK, background: PAPER }}
            aria-label="Stäng tidningen" data-testid="dagblad-close-top">✕</button>
        )}
        {!overlay && <div className="no-print"><EditionSwitch edition={edition} onChange={setEdition} /></div>}
        <Masthead p={p} />
        <div className="no-print"><SectionPicker p={p} onSaved={() => setVersion((v) => v + 1)} /></div>
        <DagbladToolbar edition={edition} />
        {ed ? <EditableLead ed={ed} edition={edition} custom={p.custom_editorial}
          onSaved={(d) => { setEd(d); setP((cur) => ({ ...cur, custom_editorial: true })); }} onReset={resetEditorial} /> : <Lead ed={null} />}
        <AssistantBox a={p.assistant} has={has} tick={tick} onHide={() => hide('assistant')} />
        <div className="grid md:grid-cols-3 gap-x-8">
          <div className="md:col-span-2 md:pr-8 md:border-r" style={{ borderColor: 'rgba(43,38,32,0.3)' }}>
            {p.kids.length > 0 && (
              <Column title={p.edition === 'evening' ? 'Lägg fram ikväll · till imorgon' : 'Kom ihåg! · Packa åt barnen'} testid="dagblad-kids" onHide={() => hide('kids')}>
                {p.kids.map((k) => (
                  <div key={k.id} className="mb-3" data-testid={`dagblad-kid-${k.name.toLowerCase()}`}>
                    <p className="font-black text-lg" style={{ fontFamily: SERIF }}>{k.reminder}</p>
                    {k.items.map((it) => (
                      <Check key={it} checked={has(`pack:${k.id}:${it}`)} onChange={() => tick(`pack:${k.id}:${it}`, !has(`pack:${k.id}:${it}`))}
                        testid={`dagblad-pack-${k.name.toLowerCase()}-${it.toLowerCase().replace(/\s+/g, '-')}`}>{it}</Check>
                    ))}
                  </div>
                ))}
              </Column>
            )}
            {p.shopping && <DagbladShopping key={`${p.date}-${p.edition}`} shop={p.shopping} evening={p.edition === 'evening'} onHide={() => hide('shopping')} />}
            {p.chores && <div className="mb-4" style={{ fontFamily: BODY }}><ChoreBoard key={`${p.date}-${p.edition}`} date={p.chores.date} initial={p.chores} onHide={() => hide('chores')} /></div>}
            {on('kidcol') && <KidsColumn p={p} onSticker={gotSticker} onHide={() => hide('kidcol')} />}
            {on('custom') && <div className={p.custom.length ? '' : 'no-print'}><CustomPoints p={p} has={has} tick={tick} onAdd={addCustom} onDelete={deleteCustom} onHide={() => hide('custom')} /></div>}
            {p.focus_room && <Column title={`${p.edition === 'evening' ? 'Imorgon städar du' : 'Idag städar du'} · ${p.focus_room.emoji} ${p.focus_room.room}`} testid="dagblad-room" onHide={() => hide('room')}>
              {p.focus_room.tips.map((t, i) => (
                <Check key={t} checked={has(`room:${i}`)} onChange={() => tick(`room:${i}`, !has(`room:${i}`))} testid={`dagblad-room-${i}`}>{t}</Check>
              ))}
            </Column>}
            {p.edition === 'morning' && on('top3') && <Column title="Viktigast idag" testid="dagblad-top3" onHide={() => hide('top3')}>
              {p.top3.length ? p.top3.map((t) => (
                <Check key={t.id} checked={t.done} onChange={() => tickTodo(t)} testid={`dagblad-todo-${t.id}`}>{t.text}</Check>
              )) : <p className="italic text-sm opacity-70" style={{ fontFamily: BODY }}>Idag-listan är tom. Då räcker det med det som står här ovanför 💛</p>}
              <InlineAdd placeholder="Lägg till att göra idag…" testid="dagblad-add-todo" onAdd={async (t) => { await addTodo(t); reload(); }} />
            </Column>}
          </div>
          <aside>
            {on('weather') && <Column title={p.edition === 'evening' ? 'Vädret just nu' : 'Vädret'} testid="dagblad-weather" onHide={() => hide('weather')}>
              {w ? (
                <div className="flex items-center gap-3">
                  <span className="text-5xl">{w.emoji}</span>
                  <div>
                    <div className="text-3xl font-black" style={{ fontFamily: SERIF }} data-testid="dagblad-temp">{Math.round(w.temp_c)}°</div>
                    <div className="text-sm" style={{ fontFamily: BODY }}>{w.label_sv}{w.city ? ` i ${w.city}` : ''}</div>
                  </div>
                </div>
              ) : <p className="italic text-sm opacity-70">Väderleken uteblev, titta ut genom fönstret ☁️</p>}
            </Column>}
            {on('events') && <Column title={p.edition === 'evening' ? 'Det här händer imorgon' : 'Det här händer idag'} testid="dagblad-events" onHide={() => hide('events')}>
              {p.events.length ? p.events.map((e, i) => (
                <div key={`${e.id}-${i}`} className="flex items-start gap-3 py-1.5 border-b border-dotted" style={{ borderColor: 'rgba(43,38,32,0.35)' }} data-testid={`dagblad-event-${i}`}>
                  <button onClick={() => tickEvent(i)} aria-label={e.completed ? 'Ångra klar' : 'Markera som klar'}
                    className="mt-0.5 w-4 h-4 flex-shrink-0 border-2 flex items-center justify-center text-[10px] font-black"
                    style={{ borderColor: INK, background: e.completed ? INK : 'transparent', color: PAPER }} data-testid={`dagblad-event-check-${i}`}>{e.completed ? '✓' : ''}</button>
                  <span className="font-black text-sm w-12 flex-shrink-0" style={{ color: OLIVE }}>{e.time || 'Heldag'}</span>
                  <span className={`text-sm ${e.completed ? 'line-through opacity-50' : ''}`} style={{ fontFamily: BODY }} data-testid={`dagblad-event-title-${i}`}>{e.important ? '★ ' : ''}{e.title}</span>
                </div>
              )) : <p className="italic text-sm opacity-70" style={{ fontFamily: BODY }}>Inget inbokat. En sällsynt lugn dag!</p>}
              {p.edition === 'morning' && p.events.length > 0 && <div className="no-print mt-2"><StreakChip testid="dagblad-streak-chip" /></div>}
              {p.upcoming?.length > 0 && (
                <div className="mt-3" data-testid="dagblad-upcoming">
                  <p className="text-[10px] font-black uppercase tracking-[0.25em] mb-1" style={{ color: '#8C2F2F' }}>Snart</p>
                  {p.upcoming.map((u, i) => (
                    <div key={`${u.id}-${u.date}`} className="flex gap-2 py-1 text-sm" style={{ fontFamily: BODY }} data-testid={`dagblad-upcoming-${i}`}>
                      <span className="font-black w-24 flex-shrink-0 whitespace-nowrap" style={{ color: OLIVE }}>{u.label}</span>
                      <span>{u.title}{u.time ? ` · ${u.time}` : ''}</span>
                    </div>
                  ))}
                </div>
              )}
              <InlineAdd placeholder={`Ny händelse ${p.edition === 'evening' ? 'imorgon' : 'idag'}…`} withTime testid="dagblad-add-event"
                onAdd={async (t, time) => { await addEvent(p.date, t, time); reload(); }} />
            </Column>}
            {p.hp && <HpBox hp={p.hp} />}
            {p.challenges.length > 0 && <ChallengesBox items={p.challenges} />}
          </aside>
        </div>
        <PlannerPages on={on} />
        <NextDayBox a={p.assistant} />
        <MissingSections p={p} onShow={show} />
        <footer className="mt-6 pt-4 border-t-[3px] border-double text-center space-y-3" style={{ borderColor: INK }}>
          <p className="italic" style={{ fontFamily: SERIF }}>{p.edition === 'evening' ? 'Lägg fram, släck lampan. Imorgon tar vi en sak i taget.' : 'Bara här och nu. Resten får vänta.'}</p>
          {overlay && (
            <button onClick={close} className="no-print px-8 py-3 text-[#F4EEE1] font-black uppercase tracking-[0.2em] text-sm transition-transform hover:scale-105"
              style={{ background: INK }} data-testid="dagblad-close">{p.edition === 'evening' ? 'God natt 🌙' : 'Nu kör vi ☕'}</button>
          )}
        </footer>
      </article>
    </div>
  );
}

export function DagbladGate() {
  const [edition, setEdition] = useState(null);
  useEffect(() => {
    axios.get(`${API}/dagblad/should-show`, { withCredentials: true }).then((r) => setEdition(r.data?.should_show ? r.data.edition : null)).catch(() => {});
  }, []);
  useEffect(() => {
    if (!edition) return undefined;
    const onNav = () => {
      axios.post(`${API}/dagblad/seen`, { edition }, { withCredentials: true }).catch(() => {});
      setEdition(null);
    };
    window.addEventListener('focus-critter:navigate', onNav);
    return () => window.removeEventListener('focus-critter:navigate', onNav);
  }, [edition]);
  return edition ? <DagbladFrontPage overlay initialEdition={edition} onClose={() => setEdition(null)} /> : null;
}
